import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Paper, Grid, Alert, Collapse, IconButton, CircularProgress } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { getCurrentUser, updateUser } from '../api/services/userService/User';

export default function Settings() {
  const { keycloak } = useKeycloak();
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    apiKey: '',
    huggingfaceToken: '',
    kaggleToken: '',
    sshKeys: [''],
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSshKeys, setShowSshKeys] = useState(false);

  const fetchCurrentUser = async () => {
    try {
      await keycloak.updateToken(30);
      const response = await getCurrentUser(keycloak.token);
      
      if (response.success) {
        const user = response.user;
        setCurrentUser(user);
        setProfile(prev => ({
          ...prev,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          phone: user.phone || '',
          company: user.company || '',
          position: user.position || '',
          // Keep the existing values for tokens and keys
          huggingfaceToken: user.huggingfaceToken || prev.huggingfaceToken,
          kaggleToken: user.kaggleToken || prev.kaggleToken,
          sshKeys: user.sshKeys || prev.sshKeys,
          apiKey: user.apiKey || prev.apiKey
        }));
      } else {
        setError('Failed to load user information');
        console.error('Failed to fetch current user:', response.error);
      }
    } catch (error) {
      setError('Error loading user information');
      console.error('Error fetching current user:', error);
    } finally {
      setUserLoading(false);
    }
  };

  useEffect(() => {
    if (keycloak.authenticated) {
      fetchCurrentUser();
    } else {
      setUserLoading(false);
    }
  }, [keycloak.authenticated]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await keycloak.updateToken(30);
      
      if (!currentUser?.id) {
        setError('User ID not found. Please refresh the page.');
        setLoading(false);
        return;
      }

      const userData = {
        id: currentUser.id,
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone,
        company: profile.company,
        position: profile.position,
        status: currentUser.status || 0
      };

      const response = await updateUser(userData, keycloak.token);
      
      if (response.success) {
        setSuccess('Settings updated successfully');
        // Refresh current user data to reflect changes
        await fetchCurrentUser();
      } else {
        setError(response.message || 'Failed to update settings');
      }

    } catch (err) {
      console.error('Update user error:', err);
      setError('Failed to update settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSshKey = () => {
    setProfile({
      ...profile,
      sshKeys: [...profile.sshKeys, '']
    });
  };

  const handleSshKeyChange = (index, value) => {
    const updatedSshKeys = [...profile.sshKeys];
    updatedSshKeys[index] = value;
    setProfile({
      ...profile,
      sshKeys: updatedSshKeys
    });
  };

  const handleRemoveSshKey = (index) => {
    const updatedSshKeys = profile.sshKeys.filter((_, i) => i !== index);
    setProfile({
      ...profile,
      sshKeys: updatedSshKeys
    });
  };

  if (userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading user information...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "100%", mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h4" gutterBottom sx={{ 
          mb: { xs: 3, sm: 4 },
          fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' }
        }}>
          Settings
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 } }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: { xs: 2, sm: 3 } }}>{success}</Alert>}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={profile.firstName}
                onChange={(e) => setProfile({...profile, firstName: e.target.value})}
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={profile.lastName}
                onChange={(e) => setProfile({...profile, lastName: e.target.value})}
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({...profile, email: e.target.value})}
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={profile.phone}
                onChange={(e) => setProfile({...profile, phone: e.target.value})}
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company"
                value={profile.company}
                onChange={(e) => setProfile({...profile, company: e.target.value})}
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Position"
                value={profile.position}
                onChange={(e) => setProfile({...profile, position: e.target.value})}
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: { xs: 1, sm: 2 },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 2 }
              }}>
                <Typography variant="h6" sx={{ 
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  SSH Keys
                </Typography>
                <IconButton
                  onClick={() => setShowSshKeys(!showSshKeys)}
                  aria-label="expand ssh keys"
                  size={window.innerWidth < 600 ? "small" : "medium"}
                >
                  {showSshKeys ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              </Box>
              
              <Collapse in={showSshKeys}>
                <Box sx={{ mt: { xs: 1, sm: 2 } }}>
                  {profile.sshKeys.map((key, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      gap: { xs: 1, sm: 2 }, 
                      mb: { xs: 1.5, sm: 2 },
                      flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                      <TextField
                        fullWidth
                        label={`SSH Key ${index + 1}`}
                        value={key}
                        onChange={(e) => handleSshKeyChange(index, e.target.value)}
                        multiline
                        rows={2}
                        size={window.innerWidth < 600 ? "small" : "medium"}
                        sx={{
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          },
                          '& .MuiOutlinedInput-root': {
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleRemoveSshKey(index)}
                        disabled={profile.sshKeys.length === 1}
                        size={window.innerWidth < 600 ? "small" : "medium"}
                        fullWidth={window.innerWidth < 600}
                        sx={{
                          minWidth: { xs: 'auto', sm: '100px' },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                  <Button
                    variant="outlined"
                    onClick={handleAddSshKey}
                    size={window.innerWidth < 600 ? "small" : "medium"}
                    fullWidth={window.innerWidth < 600}
                    sx={{
                      mt: { xs: 1, sm: 2 },
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}
                  >
                    Add SSH Key
                  </Button>
                </Box>
              </Collapse>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 3 }, 
                justifyContent: { xs: 'center', sm: 'flex-end' },
                mt: { xs: 2, sm: 3 }
              }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  size={window.innerWidth < 600 ? "medium" : "large"}
                  fullWidth={window.innerWidth < 600}
                  sx={{
                    minWidth: { xs: 'auto', sm: '120px' },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    py: { xs: 1.5, sm: 2 }
                  }}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}
